/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, April 2020
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { FormContext, useForm } from 'react-hook-form';
import useDeepCompareEffect from 'use-deep-compare-effect';
import debounce from 'debounce';
import {
  FlowBlock,
  FlowBlockDataStore,
  FlowBlockRoot,
  FlowState,
  FlowBlockAgreement,
  FlowBlockQuestion,
  Throttling,
} from '@staffino/model';
import { useTranslation } from 'react-i18next';
import { isValid } from 'date-fns';
import FlowContext from './flow-context';
import ConditionEvaluator from './condition-evaluator/condition-evaluator';
import useBridge from './use-bridge-hook';
import FlowPageLayout from './flow-page-layout';
import FlowRestarter from './flow-restarter';
import FlowStateManager from './flow-state-manager';
import useObjects from './use-objects-hook';
import FlowDefinitionTraverser from './flow-definition-traverser';
import prepareFormValues from './prepare-form-values';
import ErrorDialog from './error-dialog';
import Form from './flow-form';
import useNavigator from './use-navigator';
import useEnvironmentBridge from './use-environment-bridge';

type Props = {
  definition: FlowBlockRoot;
  status?: 'committed' | 'limit-reached' | 'expired' | 'removed' | 'ready';
  platform?: string;
};

function calculatePage(
  stateManager: FlowStateManager,
  t: (string) => string,
  throttlingRules?: Throttling[],
  status?: 'committed' | 'limit-reached' | 'expired' | 'removed' | 'ready',
) {
  let calculatedStatus = status;
  if (throttlingRules?.length) {
    for (let i = 0; i < throttlingRules.length; i += 1) {
      const rule = throttlingRules[i];
      if (
        rule.perInteractionCount &&
        rule.perInteractionLimit &&
        rule.perInteractionCount >= rule.perInteractionLimit
      ) {
        if (rule.perInteractionCount < 2) {
          calculatedStatus = 'committed';
        } else {
          calculatedStatus = 'limit-reached';
        }
      }
      if (
        rule.perCustomerCount &&
        rule.perCustomerLimit &&
        rule.perCustomerCount >= rule.perCustomerLimit
      ) {
        if (rule.perCustomerCount < 2) {
          calculatedStatus = 'committed';
        } else {
          calculatedStatus = 'limit-reached';
        }
      }
      if (
        rule.perTargetCount &&
        rule.perTargetLimit &&
        rule.perTargetCount >= rule.perTargetLimit
      ) {
        if (rule.perTargetCount < 2) {
          calculatedStatus = 'committed';
        } else {
          calculatedStatus = 'limit-reached';
        }
      }
      if (rule.requestExpiresAt) {
        const requestExpiresAt = new Date(rule.requestExpiresAt);
        if (isValid(requestExpiresAt) && new Date() > requestExpiresAt) {
          calculatedStatus = 'expired';
        }
      }
    }
  }
  switch (calculatedStatus) {
    case 'expired':
      return stateManager.getWarningPage(
        t('Your request has expired'),
        t('Your request has expired'),
      );
    case 'removed':
      return stateManager.getWarningPage(
        t('Content of this link has been removed'),
        t('It is no longer possible to submit a feedback'),
      );
    case 'committed':
      return stateManager.getWarningPage(
        t('Your feedback has already been recorded'),
        t('We are grateful for your opinion.'),
      );
    case 'limit-reached':
      return stateManager.getWarningPage(
        t('You have reached the allowed feedback limit'),
        t('We are grateful for your opinion.'),
      );
    default:
      return stateManager.getActivePage() ?? stateManager.getTerminalPage();
  }
}

function dataStoreBlock(definition): FlowBlockDataStore {
  return definition.children?.find((ch) => ch.type === 'data-store');
}

function findBlockId(questionId: string, block: FlowBlock): string {
  if (block.type === 'question') {
    const { questionId: blockQuestionId } = block as FlowBlockQuestion;
    if (questionId === blockQuestionId) {
      return block.id;
    }
  }
  const children = 'children' in block ? block.children : null;
  if (children) {
    for (let i = 0; i < children.length; i += 1) {
      const result = findBlockId(questionId, children[i] as FlowBlock);
      if (result) {
        return result;
      }
    }
  }
  return undefined;
}

function calculateInitialFormData(flowState: FlowState): object {
  const storedFormData =
    Array.isArray(flowState?.pageStates) &&
    flowState.pageStates.length > 0 &&
    flowState.pageStates[flowState.pageStates.length - 1]?.formData;

  return { ...(storedFormData || {}) };
}

export default function FlowExecutor({ definition, status, platform }: Props) {
  const bridge = useBridge();
  const environmentBridge = useEnvironmentBridge();

  // initial the values for legal agreement informative (hidden input) because of scroll effect issue
  const traverser = new FlowDefinitionTraverser(definition);
  const blocks = traverser.getBlocksByType('legal');

  const legalInformativeBlocks = blocks.filter((block) => {
    const agreementObject = bridge.objects.find(
      (o) =>
        o.objectType === 'legal-agreement' &&
        o.id === (block as FlowBlockAgreement).agreementId,
    );
    return (
      agreementObject &&
      agreementObject.objectType === 'legal-agreement' &&
      agreementObject.type === 'legal-informative'
    );
  });

  const legalDefaultValues = Object.fromEntries(
    legalInformativeBlocks.map((l) => [l.id, 1]),
  );

  const form = useForm({
    defaultValues: {
      ...legalDefaultValues,
      ...calculateInitialFormData(bridge.flowState),
    },
  });

  const { formState, reset } = form;
  const { t } = useTranslation();
  const { current: stateManager } = useRef<FlowStateManager>(
    new FlowStateManager(
      definition,
      bridge.flowState,
      bridge,
      environmentBridge,
    ),
  );

  const conditionEvaluator = useMemo(
    () => new ConditionEvaluator(stateManager, bridge, definition, form),
    [stateManager, bridge, definition],
  );
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    const debouncedUpdate = debounce(forceUpdate, 0);
    stateManager.on('update', debouncedUpdate);
    return () => {
      stateManager.off('update', debouncedUpdate);
    };
  }, [stateManager, forceUpdate]);

  useNavigator(reset, stateManager);

  const handleSubmit = useCallback(
    async (values) => {
      // commit the state if this is submitted on the last page
      if (stateManager.isLastPage()) {
        try {
          await stateManager.commit();
        } catch (error) {
          bridge.reportError(error);
          return;
        }
      }
      stateManager.updateFormDataSilently(values);
      const formValues = await stateManager.advancePage();
      if (formValues) {
        form.reset(prepareFormValues(formValues));
      }
      environmentBridge.pushHistoryState({
        index: stateManager.getCurrentPageIndex(),
      });
      environmentBridge.scrollViewportToTop();
    },
    [stateManager, form, bridge, environmentBridge],
  );

  const updateFlowSessionDebounced = useMemo(
    () => debounce(() => stateManager.updateFlowSession(), 500),
    [stateManager],
  );

  const formValues = form.watch({ nest: true });
  useDeepCompareEffect(() => {
    if (!formState.isSubmitting) {
      stateManager
        .reevaluateConditions(formValues, conditionEvaluator)
        .then(() => {
          forceUpdate();
          if (platform !== 'kiosk' && platform !== 'pos') {
            // do not save session for kiosk mode,
            // it is shared, so nothing will be saved anyway

            // better still would be to check for shared session
            updateFlowSessionDebounced();
          }
        }); // TODO: only update if needed
    }
  }, [
    formValues,
    stateManager.getTargetId(),
    stateManager,
    conditionEvaluator,
    updateFlowSessionDebounced,
    formState.isSubmitting,
  ]);

  const throttlingRules = useObjects('throttling');

  const page = calculatePage(stateManager, t, throttlingRules, status);

  const layout = useMemo(
    () =>
      definition.preferredLayout ?? dataStoreBlock(definition)?.preferredLayout,
    [definition],
  );

  const restart = useMemo(
    () => definition.restart ?? dataStoreBlock(definition)?.restart,
    [definition],
  );

  const scrollMode = useMemo(
    () => dataStoreBlock(definition)?.scrollMode,
    [definition],
  );

  const [error, setError] = useState(undefined);
  useEffect(() => {
    bridge.on('error', setError);
    return () => {
      bridge.off('error', setError);
    };
  }, [bridge, setError]);

  return (
    <FormContext {...form}>
      <Form method="post" onSubmit={form.handleSubmit(handleSubmit)}>
        <FlowContext.Provider
          value={{ stateManager, definition, handleSubmit }}
        >
          <FlowPageLayout
            page={page}
            key={page.id}
            layout={layout}
            scrollMode={scrollMode}
          />
          {restart && <FlowRestarter restart={restart} />}
          {error && (
            <ErrorDialog error={error} onClose={() => setError(undefined)} />
          )}
        </FlowContext.Provider>
      </Form>
    </FormContext>
  );
}
